import { Chip, Stack } from "@mui/material";
import { formatDuration } from "date-fns";

import { Grid, Typography, TypographyProps } from "@/components/ui-v2";
import { CopyToClipboard } from "@/components/ui-v2/CopyToClipboard/CopyToClipboard";
import { getDisplayPrompt } from "@/utils/getDisplayPrompt";
import { humanFileSize } from "@/utils/humanize";
import { getObjectType } from "@/utils/libraryObjectUtils";

import { ForgeInfoStyles } from "./ForgeInfoStyles";
import { ReferenceFiles } from "./ReferenceFile";
import { FileWithDetails, InfoEntry } from "./types";
import { VideoEffect } from "./VideoEffect";

const BasicInfoTypography = ({ children, ...rest }: TypographyProps) => (
  <Typography color="secondary.1" variant="small" fontWeight={500} {...rest}>
    {children}
  </Typography>
);

const getCommonFileInfo = (file: FileWithDetails): InfoEntry[] => {
  const basicInfo: InfoEntry[] = [];
  if (file.sizeBytes) {
    basicInfo.push({
      id: "size",
      label: "Size",
      value: (
        <BasicInfoTypography>
          {humanFileSize(file.sizeBytes)}
        </BasicInfoTypography>
      ),
    });
  }

  return basicInfo;
};

const getCommon2DInfo = (file: FileWithDetails): InfoEntry[] => {
  const basicInfo: InfoEntry[] = [];

  if (file.metadata?.width && file.metadata?.height) {
    basicInfo.push({
      id: "dimensions",
      label: "Dimensions",
      value: (
        <BasicInfoTypography>{`${file.metadata.width} × ${file.metadata.height}`}</BasicInfoTypography>
      ),
    });
  }

  if (file.sizeBytes) {
    basicInfo.push({
      id: "size",
      label: "Size",
      value: (
        <BasicInfoTypography>
          {humanFileSize(file.sizeBytes)}
        </BasicInfoTypography>
      ),
    });
  }

  return basicInfo;
};

const getCommonVideoInfo = (file: FileWithDetails): InfoEntry[] => {
  const basicInfo: InfoEntry[] = [];

  if (file.metadata?.width && file.metadata?.height) {
    basicInfo.push({
      id: "dimensions",
      label: "Dimensions",
      value: (
        <BasicInfoTypography>{`${file.metadata.width} × ${file.metadata.height}`}</BasicInfoTypography>
      ),
    });
  }

  if (file.metadata?.durationSeconds) {
    basicInfo.push({
      id: "duration",
      label: "Duration",
      value: (
        <BasicInfoTypography>
          {formatDuration({
            seconds: file.metadata.durationSeconds,
          })}
        </BasicInfoTypography>
      ),
    });
  }

  if (file.metadata?.codecName) {
    basicInfo.push({
      id: "codec",
      label: "Codec",
      value: (
        <BasicInfoTypography>{file.metadata.codecName}</BasicInfoTypography>
      ),
    });
  }

  if (file.sizeBytes) {
    basicInfo.push({
      id: "size",
      label: "Size",
      value: (
        <BasicInfoTypography>
          {humanFileSize(file.sizeBytes)}
        </BasicInfoTypography>
      ),
    });
  }

  return basicInfo;
};

const getCommonAudioInfo = (file: FileWithDetails): InfoEntry[] => {
  const basicInfo: InfoEntry[] = [];

  if (file.metadata?.durationSeconds) {
    basicInfo.push({
      id: "duration",
      label: "Duration",
      value: (
        <BasicInfoTypography>
          {file.metadata.durationSeconds.toFixed(2)} seconds
        </BasicInfoTypography>
      ),
    });
  }

  if (file.metadata?.codecName) {
    basicInfo.push({
      id: "codec",
      label: "Codec",
      value: (
        <BasicInfoTypography>{file.metadata.codecName}</BasicInfoTypography>
      ),
      tooltip: "Codec used to encode the audio",
    });
  }

  if (file.metadata?.bitrateKbps) {
    basicInfo.push({
      id: "bit-rate",
      label: "Bit Rate",
      value: (
        <BasicInfoTypography>
          {file.metadata.bitrateKbps.toFixed()} kbps
        </BasicInfoTypography>
      ),
    });
  }

  if (file.metadata?.sampleRate) {
    basicInfo.push({
      id: "sample-rate",
      label: "Sample Rate",
      value: (
        <BasicInfoTypography>{file.metadata.sampleRate} Hz</BasicInfoTypography>
      ),
    });
  }

  if (file.metadata?.numChannels) {
    basicInfo.push({
      id: "num-channels",
      label: "Channels",
      value: (
        <BasicInfoTypography>{file.metadata.numChannels}</BasicInfoTypography>
      ),
    });
  }

  if (file.metadata?.bitDepth) {
    basicInfo.push({
      id: "bit-depth",
      label: "Bit Depth",
      value: (
        <BasicInfoTypography>{file.metadata.bitDepth} bits</BasicInfoTypography>
      ),
    });
  }

  return basicInfo;
};

const getStylesInfo = (file: FileWithDetails): InfoEntry[] => {
  const stylesInfo: InfoEntry[] = [];

  if (file.styles?.length) {
    stylesInfo.push({
      id: "styles",
      label: file.styles.length === 1 ? "Model" : "Models",
      value: (
        <ForgeInfoStyles
          styles={file.styles}
          stylesParams={file.inferenceParameters?.styles}
        />
      ),
    });
  }

  return stylesInfo;
};

const getEffectsInfo = (file: FileWithDetails): InfoEntry[] => {
  const effectsInfo: InfoEntry[] = [];

  if (file.inferenceParameters?.videoEffects?.length) {
    const effects = file.inferenceParameters?.videoEffects;

    effectsInfo.push({
      id: "effects",
      label: "Effects",
      value: (
        <Grid container spacing={1}>
          {effects.map((effect) => (
            <Grid item xs={6} key={effect.type}>
              <VideoEffect effect={effect} />
            </Grid>
          ))}
        </Grid>
      ),
    });
  }

  return effectsInfo;
};

const getCommon3DInfo = (file: FileWithDetails): InfoEntry[] => {
  const basicInfo: InfoEntry[] = [];

  if (file.sizeBytes) {
    basicInfo.push({
      id: "size",
      label: "Size",
      value: (
        <BasicInfoTypography>
          {humanFileSize(file.sizeBytes)}
        </BasicInfoTypography>
      ),
    });
  }

  if (file.metadata?.numVertices) {
    basicInfo.push({
      id: "vertices",
      label: "Vertices",
      value: (
        <BasicInfoTypography>
          {file.metadata.numVertices.toLocaleString()}
        </BasicInfoTypography>
      ),
    });
  }

  if (file.metadata?.numFaces) {
    basicInfo.push({
      id: "faces",
      label: "Faces",
      value: (
        <BasicInfoTypography>
          {file.metadata.numFaces.toLocaleString()}
        </BasicInfoTypography>
      ),
    });
  }

  const features: string[] = [];
  if (file.inferenceParameters?.pbrMaterials) {
    features.push("PBR Materials");
  }
  if (file.inferenceParameters?.quadMesh) {
    features.push("Quad Mesh");
  }
  if (file.inferenceParameters?.generateParts) {
    features.push("Generate Parts");
  }
  if (file.inferenceParameters?.lowPoly) {
    features.push("Low Poly");
  }
  if (file.inferenceParameters?.enableRigging) {
    features.push("Rigging");
  }
  if (file.inferenceParameters?.enableAnimation) {
    features.push("Animation");
  }
  if (features.length > 0) {
    basicInfo.push({
      id: "features",
      label: "Features",
      value: (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flexWrap: "wrap",
          }}
        >
          {features.map((badge) => (
            <Chip key={badge} color="secondary" label={badge} size="small" />
          ))}
        </Stack>
      ),
    });
  }

  return basicInfo;
};

const getReferenceCommonInfo = (file: FileWithDetails): InfoEntry[] => {
  const basicInfo: InfoEntry[] = [];
  const inferenceParameters = file.inferenceParameters;
  const files = file.inferenceParameters?.files || [];
  if (inferenceParameters && files?.length > 0) {
    basicInfo.push({
      id: "reference-image",
      label: undefined,
      value: (
        <ReferenceFiles
          inferenceParameters={inferenceParameters}
          files={files}
        />
      ),
    });
  }
  return basicInfo;
};

const getPromptInfo = (file: FileWithDetails): InfoEntry[] => {
  const basicInfo: InfoEntry[] = [];

  const prompt = getDisplayPrompt(file.inferenceParameters);
  if (prompt) {
    basicInfo.push({
      id: "prompt",
      label: "Prompt",
      value: (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <BasicInfoTypography noWrap title={prompt}>
            {prompt}
          </BasicInfoTypography>
          <CopyToClipboard
            rounded
            size="small"
            value={prompt}
            successMessage="Successfully copied prompt to clipboard."
          />
        </Stack>
      ),
    });
  }

  return basicInfo;
};

const get2DInfo = (file: FileWithDetails): InfoEntry[] => {
  let basicInfo: InfoEntry[] = [];

  basicInfo = basicInfo.concat(getPromptInfo(file));
  basicInfo = basicInfo.concat(getReferenceCommonInfo(file));
  basicInfo = basicInfo.concat(getStylesInfo(file));
  basicInfo = basicInfo.concat(getCommon2DInfo(file));

  return basicInfo;
};

const getVideoInfo = (file: FileWithDetails): InfoEntry[] => {
  let basicInfo: InfoEntry[] = [];

  basicInfo = basicInfo.concat(getPromptInfo(file));
  basicInfo = basicInfo.concat(getReferenceCommonInfo(file));
  basicInfo = basicInfo.concat(getStylesInfo(file));
  basicInfo = basicInfo.concat(getEffectsInfo(file));
  basicInfo = basicInfo.concat(getCommonVideoInfo(file));

  return basicInfo;
};

const get3DInfo = (file: FileWithDetails): InfoEntry[] => {
  let basicInfo: InfoEntry[] = [];

  basicInfo = basicInfo.concat(getPromptInfo(file));
  basicInfo = basicInfo.concat(getReferenceCommonInfo(file));
  basicInfo = basicInfo.concat(getStylesInfo(file));
  basicInfo = basicInfo.concat(getCommon3DInfo(file));

  return basicInfo;
};

const getAudioInfo = (file: FileWithDetails): InfoEntry[] => {
  let basicInfo: InfoEntry[] = [];

  basicInfo = basicInfo.concat(getPromptInfo(file));
  basicInfo = basicInfo.concat(getStylesInfo(file));
  basicInfo = basicInfo.concat(getCommonAudioInfo(file));

  return basicInfo;
};

export const getFileBasicInfo = (file: FileWithDetails): InfoEntry[] => {
  switch (getObjectType(file)) {
    case "image":
      return get2DInfo(file);
    case "video":
      return getVideoInfo(file);
    case "mesh":
      return get3DInfo(file);
    case "audio":
      return getAudioInfo(file);
    default:
      return getCommonFileInfo(file);
  }
};

export const getFileAdvancedInfo = (file: FileWithDetails): InfoEntry[] => {
  const info = [];

  if (file.inferenceParameters) {
    const params = file.inferenceParameters;
    if (params.seed) {
      info.push({
        id: "seed",
        label: "Seed",
        value: <BasicInfoTypography>{params.seed}</BasicInfoTypography>,
      });
    }
    if (params.numInferenceSteps) {
      info.push({
        id: "numInferenceSteps",
        label: "Inference Steps",
        value: (
          <BasicInfoTypography>{params.numInferenceSteps}</BasicInfoTypography>
        ),
      });
    }
    if (params.guidanceScale) {
      info.push({
        id: "guidanceScale",
        label: "Guidance Scale",
        value: (
          <BasicInfoTypography>
            {params.guidanceScale.toFixed(2)}
          </BasicInfoTypography>
        ),
      });
    }
    if (params.baseShift) {
      info.push({
        id: "baseShift",
        label: "Base Shift",
        value: (
          <BasicInfoTypography>
            {params.baseShift.toFixed(2)}
          </BasicInfoTypography>
        ),
      });
    }
    if (params.maxShift) {
      info.push({
        id: "maxShift",
        label: "Max Shift",
        value: (
          <BasicInfoTypography>
            {params.maxShift.toFixed(2)}
          </BasicInfoTypography>
        ),
      });
    }
    if (params.creativity) {
      info.push({
        id: "creativity",
        label: "Creativity",
        value: (
          <BasicInfoTypography>{params.creativity * 100}%</BasicInfoTypography>
        ),
      });
    }
    if (params.resemblance) {
      info.push({
        id: "resemblance",
        label: "Resemblance",
        value: (
          <BasicInfoTypography>{params.resemblance * 100}%</BasicInfoTypography>
        ),
      });
    }
    if (params.transparency) {
      info.push({
        id: "transparency",
        label: undefined,
        value: <BasicInfoTypography>Transparent</BasicInfoTypography>,
      });
    }
    if (params.tileability) {
      info.push({
        id: "tileability",
        label: undefined,
        value: <BasicInfoTypography>Tiled</BasicInfoTypography>,
      });
    }

    if (typeof params.promptStrength === "number") {
      info.push({
        id: "promptStrength",
        label: "Prompt Strength",
        value: (
          <BasicInfoTypography>
            {params.promptStrength * 100}%
          </BasicInfoTypography>
        ),
      });
    }

    if (typeof params.stability === "number") {
      info.push({
        id: "stability",
        label: "Stability",
        value: (
          <BasicInfoTypography>{params.stability * 100}%</BasicInfoTypography>
        ),
      });
    }

    if (typeof params.speed === "number") {
      info.push({
        id: "speed",
        label: "Speed",
        value: <BasicInfoTypography>{params.speed}</BasicInfoTypography>,
      });
    }

    if (typeof params.useSpeakerBoost === "boolean") {
      info.push({
        id: "useSpeakerBoost",
        label: "Speaker Boost",
        value: (
          <BasicInfoTypography>
            {params.useSpeakerBoost ? "Yes" : "No"}
          </BasicInfoTypography>
        ),
      });
    }

    if (typeof params.similarityBoost === "number") {
      info.push({
        id: "similarityBoost",
        label: "Similarity Boost",
        value: (
          <BasicInfoTypography>
            {params.similarityBoost * 100}%
          </BasicInfoTypography>
        ),
      });
    }

    if (typeof params.styleExaggeration === "number") {
      info.push({
        id: "styleExaggeration",
        label: "Style Exaggeration",
        value: (
          <BasicInfoTypography>
            {params.styleExaggeration * 100}%
          </BasicInfoTypography>
        ),
      });
    }
  }

  return info;
};
